'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import useDebounce from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ComponentProps, FC, useEffect, useEffectEvent } from 'react';
import { useForm } from 'react-hook-form';

export const SelectorWrapper: FC<ComponentProps<'section'>> = ({ className, ...rest }) => {
  return (
    <section className={cn('flex h-full flex-col overflow-x-auto text-sm', className)} {...rest} />
  );
};

export const AllItemsCheckbox: FC<ComponentProps<'div'> & { checked: CheckedState }> = ({
  children,
  checked,
  className,
  ...rest
}) => {
  return (
    <div
      /* The nowrap is to not have a weird animation during opening/closing of sidebar */
      className={cn(
        'inline-flex h-7 shrink-0 cursor-pointer flex-nowrap items-center gap-x-2 pl-4 text-nowrap select-none',
        className,
      )}
      {...rest}
    >
      <Checkbox checked={checked ?? false} />
      <p>{children}</p>
    </div>
  );
};

export const SelectorSearchBar: FC<{
  placeholderText: string;
  searchAction: (s: string) => void;
  classNames?: { container?: string; input?: string };
  storeSearchValue?: string;
}> = ({ placeholderText: placeholder, searchAction, classNames, storeSearchValue }) => {
  const { register, watch, setValue } = useForm({
    defaultValues: { search: storeSearchValue ?? '' },
  });
  // eslint-disable-next-line react-hooks/incompatible-library
  const search = watch('search');
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (storeSearchValue === undefined) return;
    setValue('search', storeSearchValue);
  }, [setValue, storeSearchValue]);

  const callSearchAction = useEffectEvent(() => {
    if (debouncedSearch !== search) return;
    searchAction(debouncedSearch);
  });

  useEffect(() => {
    callSearchAction();
  }, [debouncedSearch, search]);

  return (
    <div className={cn('px-4 py-8', classNames?.container)}>
      <Input
        className={cn('h-10 rounded-full bg-white', classNames?.input)}
        type="text"
        placeholder={placeholder}
        {...register('search')}
      />
    </div>
  );
};

const Divider: FC<ComponentProps<'div'>> = ({ className, ...rest }) => (
  <div className={cn('flex flex-col gap-y-1 pl-4', className)} {...rest} />
);

const ItemSkeleton: FC<ComponentProps<typeof Skeleton>> = ({ className, ...rest }) => (
  <Skeleton className={cn('h-7 w-full bg-gray-200', className)} {...rest} />
);

export const SelectorSkeleton = () => {
  return (
    <Divider className="px-4">
      <ItemSkeleton />
      {[...Array(3).keys()].map((i) => (
        <Divider key={i}>
          <ItemSkeleton key={i} />
          <Divider>
            {[...Array(3).keys()].map((i) => (
              <ItemSkeleton key={i} />
            ))}
          </Divider>
        </Divider>
      ))}
      <ItemSkeleton />
      <Divider>
        {[...Array(3).keys()].map((i) => (
          <ItemSkeleton key={i} />
        ))}
      </Divider>
    </Divider>
  );
};
